<script setup>
import { ref, onMounted } from 'vue';
import {
  useStore,
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import InstructionEditor from './InstructionEditor.vue';
import InstructionSkeletonLoader from './InstructionSkeletonLoader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import htmlToMarkdownConverter from './HtmlToMarkdownConverter.js';

const props = defineProps({
  assistantId: {
    type: Number,
    default: null,
  },
});

const store = useStore();

const showDeleteConfirmation = ref(false);
const selectedInstructionKey = ref(null);

const instructions = useMapGetter('instructions/getInstructions');
const uiFlags = useMapGetter('instructions/getUIFlags');
const activeAssistant = useFunctionGetter(
  'assistants/getAssistant',
  props.assistantId
);

async function fetchInstructions() {
  try {
    await store.dispatch(
      'instructions/fetchInstructions',
      activeAssistant.value.id
    );
  } catch (e) {
    // Handle error appropriately
  }
}

onMounted(() => {
  fetchInstructions();
});

async function addEmptyInstruction() {
  try {
    await store.dispatch('instructions/createInstruction', {
      rookooAgentId: activeAssistant.value.id,
      instruction: { content: '' },
    });
    fetchInstructions();
  } catch (e) {
    // Handle error appropriately
  }
}

function openDeletePopup(key) {
  selectedInstructionKey.value = key;
  showDeleteConfirmation.value = true;
}

function closeDeletePopup() {
  showDeleteConfirmation.value = false;
  selectedInstructionKey.value = null;
}

async function confirmDelete() {
  try {
    await store.dispatch('instructions/deleteInstruction', {
      rookooAgentId: activeAssistant.value.id,
      id: selectedInstructionKey.value,
    });
    closeDeletePopup();
    fetchInstructions();
  } catch (e) {
    // Handle error appropriately
  }
}

async function handleUpdateInstruction(updatedInstruction) {
  try {
    const markdownContent = htmlToMarkdownConverter.convertToMarkdown(
      updatedInstruction.content
    );

    await store.dispatch('instructions/updateInstruction', {
      rookooAgentId: activeAssistant.value.id,
      id: updatedInstruction.key,
      instruction: { content: markdownContent },
    });
    // No need to refresh since it's autosave
  } catch (e) {
    // Handle error appropriately
  }
}

function handleDeleteInstruction(instruction) {
  openDeletePopup(instruction.key);
}
</script>

<template>
  <div>
    <SettingsSection
      :title="$t('ASSISTANT_MGMT.INSTRUCTIONS.ASSISTANT_INSTRUCTIONS_TITLE')"
      :sub-title="
        $t('ASSISTANT_MGMT.INSTRUCTIONS.ASSISTANT_INSTRUCTIONS_SUB_TEXT')
      "
    >
      <!-- Skeleton Loader -->
      <InstructionSkeletonLoader
        v-if="uiFlags.isFetching && !uiFlags.hasInitialLoad"
      />

      <div v-else-if="instructions.length" class="instructions-list mb-8">
        <div
          v-for="(instruction, index) in instructions"
          :key="instruction.key"
          class="instruction-item mb-4 p-4 border border-n-slate-3 dark:border-n-slate-8 rounded-lg bg-white dark:bg-n-slate-2"
        >
          <div class="flex items-center justify-between mb-3 group">
            <span
              class="text-sm font-bold text-slate-800 dark:text-slate-100 bg-n-slate-3 dark:bg-n-slate-7 px-2 py-1 rounded"
            >
              {{ `Instruction ${index + 1}` }}
            </span>
            <div class="opacity-0 group-hover:opacity-100 transition-opacity">
              <NextButton
                v-tooltip.top="$t('ASSISTANT_MGMT.DELETE.BUTTON_TEXT')"
                icon="i-lucide-x"
                ghost
                sm
                @click="handleDeleteInstruction(instruction)"
              />
            </div>
          </div>

          <InstructionEditor
            :instruction="instruction"
            :show-delete-button="false"
            @update="handleUpdateInstruction"
          />
        </div>
      </div>

      <div class="add-instruction-section">
        <NextButton
          class="mb-4"
          :label="$t('ASSISTANT_MGMT.INSTRUCTIONS.ADD')"
          icon="i-lucide-plus"
          @click="addEmptyInstruction"
        />
      </div>
    </SettingsSection>

    <!-- Confirmation Dialog -->
    <woot-delete-modal
      v-model:show="showDeleteConfirmation"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDelete"
      :title="$t('ASSISTANT_MGMT.DELETE.INSTRUCTIONS.TITLE')"
      :message="$t('ASSISTANT_MGMT.DELETE.INSTRUCTIONS.MESSAGE')"
      :confirm-text="$t('ASSISTANT_MGMT.DELETE.INSTRUCTIONS.YES')"
      :reject-text="$t('ASSISTANT_MGMT.DELETE.INSTRUCTIONS.NO')"
    />
  </div>
</template>
